import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  IconButton,
  InputAdornment,
  FormControlLabel,
  Switch,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";
import VisibilityIcon from "@material-ui/icons/Visibility";
import VisibilityOffIcon from "@material-ui/icons/VisibilityOff";
import CircularProgress from "@material-ui/core/CircularProgress";
import api from "../../services/api";
import toastError from "../../errors/toastError";

const useStyles = makeStyles((theme) => ({
  btnWrapper: { position: "relative" },
  buttonProgress: {
    color: green[500],
    position: "absolute",
    top: "50%",
    left: "50%",
    marginTop: -12,
    marginLeft: -12,
  },
  dialogTitle: {
    background: "#065f46",
    color: "white",
    padding: theme.spacing(2),
  },
  infoBox: {
    backgroundColor: "#d1fae5",
    border: "1px solid #6ee7b7",
    borderRadius: 8,
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  infoTitle: {
    fontWeight: 600,
    color: "#065f46",
    marginBottom: 4,
  },
  infoText: {
    color: "#555",
    fontSize: "0.875rem",
    lineHeight: 1.6,
  },
}));

const EMPTY = { baseUrl: "", token: "", ativo: true };

const SysClinicaModal = ({ open, onClose, onSave }) => {
  const classes = useStyles();
  const [config, setConfig] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [showToken, setShowToken] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get("/sys-clinica-integration")
      .then(({ data }) => {
        if (data) {
          setConfig({
            baseUrl: data.baseUrl || "",
            token: data.token || "",
            ativo: data.ativo !== undefined ? data.ativo : true,
          });
        } else {
          setConfig(EMPTY);
        }
      })
      .catch(() => setConfig(EMPTY));
  }, [open]);

  const handleClose = () => {
    setConfig(EMPTY);
    setShowToken(false);
    onClose();
  };

  const handleSave = async () => {
    if (config.ativo && !config.baseUrl.trim()) {
      toast.error("Informe a URL base do Sys Clinica.");
      return;
    }
    if (config.ativo && !config.token.trim()) {
      toast.error("Informe o Token de acesso.");
      return;
    }

    try {
      setLoading(true);
      await api.post("/sys-clinica-integration", {
        baseUrl: config.baseUrl.trim(),
        token: config.token.trim(),
        ativo: config.ativo,
      });
      toast.success("Configuração Sys Clinica salva com sucesso!");
      if (onSave) onSave();
      handleClose();
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  const set = (field) => (e) =>
    { e.persist(); setConfig((prev) => ({ ...prev, [field]: e.target.value })); };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm" scroll="paper">
      <DialogTitle className={classes.dialogTitle}>
        Configurar Integração Sys Clinica
      </DialogTitle>

      <DialogContent>
        <Box className={classes.infoBox}>
          <Typography className={classes.infoTitle}>Sobre o Sys Clinica</Typography>
          <Typography className={classes.infoText}>
            O Sys Clinica permite integrar seu sistema de gestão de clínica diretamente
            ao Floow Chat. Configure a URL base da API e o token de acesso abaixo.
          </Typography>
        </Box>

        <TextField
          label="URL base do Sys Clinica"
          placeholder="https://api.sysclinica.com.br"
          value={config.baseUrl}
          onChange={set("baseUrl")}
          fullWidth
          margin="dense"
          variant="outlined"
          helperText="Sem barra no final. Ex: https://api.sysclinica.com.br"
        />

        <TextField
          label="Token de acesso"
          type={showToken ? "text" : "password"}
          value={config.token}
          onChange={set("token")}
          fullWidth
          margin="dense"
          variant="outlined"
          helperText="Token gerado nas configurações do Sys Clinica"
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={() => setShowToken((v) => !v)} edge="end">
                  {showToken ? <VisibilityOffIcon /> : <VisibilityIcon />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        <Box style={{ marginTop: 12 }}>
          <FormControlLabel
            control={
              <Switch
                checked={config.ativo}
                onChange={(e) => { e.persist(); setConfig((prev) => ({ ...prev, ativo: e.target.checked })); }}
                color="primary"
              />
            }
            label="Ativar integração"
          />
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} color="secondary" disabled={loading}>
          Cancelar
        </Button>
        <Box className={classes.btnWrapper}>
          <Button
            onClick={handleSave}
            color="primary"
            variant="contained"
            disabled={loading}
          >
            Salvar Configuração
          </Button>
          {loading && <CircularProgress size={24} className={classes.buttonProgress} />}
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default SysClinicaModal;
